import Parser from "papaparse";

interface JsonToCSVOptions {
  header?: boolean;
  columns?: string[];
}

/**
 * Takes an array of objects and returns a CSV-formatted string.
 */
export function jsonToCSV<T>(data: T[], options: JsonToCSVOptions = {}): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const csvData = Parser.unparse(data as any, {
        header: options.header ?? true,
        columns: options.columns,
      });

      resolve(csvData);
    } catch (e) {
      reject(e);
    }
  });
}

export interface CsvFileToJSONOptions<T> {
  header?: boolean;
  cleanHeader?: boolean;
  delimiter?: Parser.ParseConfig<T>["delimiter"];
}

/**
 * Takes in a CSV file from the Web File API or Node ReadableStream and returns JSON representation.
 * @param file - The CSV file to parse.
 * @param options Options to pass to the parser.
 * @param options.header Declare if a header is present. Defaults to true.
 * @param options.cleanHeader Declare if headers are clean and do not need transformation. Defaults to true.
 * @param options.delimiter Override the default comma delimiter.
 * @return JSON representation of CSV file.
 */
export function csvToJSON<T, TFile extends Parser.LocalFile = Parser.LocalFile>(
  file: TFile,
  options: CsvFileToJSONOptions<T> = {},
): Promise<Parser.ParseResult<T>> {
  const { header = true, cleanHeader = true, delimiter } = options;
  return new Promise((resolve, reject) => {
    const transformHeader = cleanHeader
      ? (header: string) => {
          let hdr = header.trim();
          if (hdr.startsWith('"') && hdr.endsWith('"')) {
            hdr = hdr.slice(1, -1);
          }
          return hdr.toLowerCase();
        }
      : undefined;

    Parser.parse(file, {
      delimiter,
      header,
      skipEmptyLines: true,
      transformHeader,
      complete(res: Parser.ParseResult<T>) {
        resolve(res);
      },
      error(err) {
        reject(err);
      },
    });
  });
}

export function base64CSVToJSON<T>(
  base64Csv: string,
  options: CsvFileToJSONOptions<T> = {},
): Promise<Parser.ParseResult<T>> {
  const csvContent = Buffer.from(base64Csv.trim(), "base64").toString("utf-8");
  return new Promise((resolve, reject) => {
    const { header = true, cleanHeader = true, delimiter } = options;
    const transformHeader = cleanHeader
      ? (hdr: string) => {
          let s = hdr.trim();
          if (s.startsWith('"') && s.endsWith('"')) {
            s = s.slice(1, -1);
          }
          return s.toLowerCase();
        }
      : undefined;

    Parser.parse(csvContent, {
      delimiter,
      header,
      skipEmptyLines: true,
      transformHeader,
      complete(res: Parser.ParseResult<T>) {
        resolve(res);
      },
      error(err: Error) {
        reject(err);
      },
    });
  });
}

export enum CSVColumnParsingMethod {
  OPTION_SET_ID = "OPTION_SET_ID",
  CHARACTER = "CHARACTER",
  INDEX = "INDEX",
}

interface CSVColumnParsingBase {
  readonly method: CSVColumnParsingMethod;
  readonly fromEnd?: boolean;
}

export function isCSVColumnParsing(columnParsing: any): columnParsing is CSVColumnParsing {
  return isCSVColumnCharacterParsing(columnParsing) || isCSVColumnIndexParsing(columnParsing);
}

export interface CSVColumnCharacterParsing extends CSVColumnParsingBase {
  readonly method: CSVColumnParsingMethod.CHARACTER;
  readonly character: string;
}

export function isCSVColumnCharacterParsing(column: CSVColumnParsing): column is CSVColumnCharacterParsing {
  return column?.method === CSVColumnParsingMethod.CHARACTER;
}

export interface CSVColumnIndexParsing extends CSVColumnParsingBase {
  readonly method: CSVColumnParsingMethod.INDEX;
  readonly index: number;
}

export function isCSVColumnIndexParsing(column: CSVColumnParsing): column is CSVColumnIndexParsing {
  return column?.method === CSVColumnParsingMethod.INDEX;
}

export interface CSVColumnOptionSetIDParsing extends CSVColumnParsingBase {
  readonly method: CSVColumnParsingMethod.OPTION_SET_ID;
}

export function isCSVColumnOptionSetIDParsing(column: CSVColumnParsing): column is CSVColumnOptionSetIDParsing {
  return column?.method === CSVColumnParsingMethod.OPTION_SET_ID;
}

export type CSVColumnParsing = CSVColumnCharacterParsing | CSVColumnIndexParsing | CSVColumnOptionSetIDParsing;

export function applyCSVColumnParsingToField(field: string, columnParsing: CSVColumnParsing): string {
  if (!isCSVColumnParsing(columnParsing)) {
    console.warn("Invalid column parsing method");
    return field;
  }

  if (isCSVColumnOptionSetIDParsing(columnParsing)) {
    return field;
  }

  if (isCSVColumnCharacterParsing(columnParsing)) {
    const { character, fromEnd } = columnParsing;

    if (field.indexOf(character) === -1) {
      console.warn(`Attempting to parse a field that does not contain the character: ${character}`);
      return field;
    }

    if (fromEnd) {
      return field.split(character).pop() as string;
    } else {
      return field.split(character).shift() as string;
    }
  }

  if (isCSVColumnIndexParsing(columnParsing)) {
    const { index, fromEnd } = columnParsing;

    if (field.length < index || index < 0) {
      console.warn(`Attempting to parse a field that does not contain the index: ${index}`);
      return field;
    }

    if (fromEnd) {
      return field.slice(field.length - index);
    } else {
      return field.slice(0, index);
    }
  }

  const out: never = columnParsing;
  return out;
}

export interface CSVColumnMapping {
  readonly groupIdentifier?: string;
  readonly [key: string]: string | undefined;
}

export function sanitizeColumnMapping(mapping: CSVColumnMapping): CSVColumnMapping {
  const sanitizedMapping: Record<string, string | undefined> = {};

  for (const field in mapping) {
    if (mapping[field]) {
      sanitizedMapping[field] = mapping[field] ? String(mapping[field]).trim().toLowerCase() : "";
    }
  }

  return sanitizedMapping;
}

export interface CSVUploadSchemaFields {
  readonly required?: boolean;
  readonly groupByColumn?: boolean;
  readonly groupByDescription?: string;
  readonly label: string;
  readonly value: string;
  readonly description?: string;
}
